<template>
  <div id="recovery-activity" class="brand-card-lg">
    <div v-if="initialLoading">
      <HeaderLoader />
    </div>
    <div v-else>
      <div class="sm:flex sm:items-center sm:justify-between space-y-3 pb-5 sm:space-y-0">
        <div class="space-x-6 flex items-center justify-between flex-grow">
          <div class="inline">
            <p class="title mb-1">Recovery Breakdowns</p>
          </div>
        </div>
        <div class="flex justify-end flex-grow">
          <div class="flex items-center space-x-2 brand-input">
            <CampaignFilter class="flex-shrink-0" @update="onFilterUpdate" :include="campaignFilters" :preselected="['trial.false']" />
            <!-- aggregation options -->
            <el-select v-model="countOrAmount" placeholder="Show" @change="(value) => (showPercent = value)">
              <el-option value="amount" label="By Payment Amount ($)" />
              <el-option value="count" label="By # of Payments" />
            </el-select>
            <el-select v-model="percentOrRaw" placeholder="Show">
              <el-option value="raw" :label="countOrAmount === 'count' ? 'Total Number (#)' : 'Total Amount ($)'" />
              <el-option value="percent" label="Percentage (%)" />
            </el-select>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div v-if="loading" class="-ml-4">
        <SessionListLoader />
      </div>
      <div v-if="counts.length">
        <div class="grid space-x-4 grid-cols-2 mt-4">
          <!-- Reason for Decline -->
          <div class="space-y-2">
            <p class="text-base font-bold text-gray-800 ml-1">Failed Payments by Reason for Decline</p>
            <div class="text-sm text-brand-brown font-semibold ml-1">
              <p v-if="percentOrRaw === 'percent' && countOrAmount === 'count'">Percentage of failed payments by reason for decline.</p>
              <p v-else-if="percentOrRaw === 'percent' && countOrAmount === 'amount'">Percentage of total failures by invoice amount ($).</p>
              <p v-if="percentOrRaw === 'raw' && countOrAmount === 'count'">Total number of failed payments.</p>
              <p v-else-if="percentOrRaw === 'raw' && countOrAmount === 'amount'">Total invoice amount ($) of failed payments.</p>
            </div>
            <BasicTable compressed :columns="failReasonCols" :data="declineReasonDisplayedRows" idProp="value" />
            <el-button v-show="declineReasonsHidden" type="text" size="mini" class="ml-1" @click="declineReasonsExpanded = true"
              >Expand all {{ declineReasonRows.length }} reasons ↓</el-button
            >
          </div>
          <!-- Recovery Rates -->
          <div class="space-y-2">
            <p class="text-base font-bold text-gray-800 ml-1">{{ percentOrRaw === 'raw' ? 'Recovered Payments' : 'Recovery Rates' }}</p>
            <div class="text-sm text-brand-brown font-semibold ml-1">
              <p v-if="percentOrRaw === 'percent' && countOrAmount === 'count'">
                Percentage of completed campaigns that successfully recovered failed payments.
              </p>
              <p v-else-if="percentOrRaw === 'percent' && countOrAmount === 'amount'">
                Percentage of total invoice failures ($) that that has been recovered.
              </p>
              <p v-if="percentOrRaw === 'raw' && countOrAmount === 'count'">Number of successfully recovered payments.</p>
              <p v-else-if="percentOrRaw === 'raw' && countOrAmount === 'amount'">Total invoice amount recovered after failed payments.</p>
            </div>
            <BasicTable compressed zeroPadding :columns="failReasonRecoverCols" :data="declineReasonDisplayedRows" idProp="value" />
          </div>
        </div>
        <div v-if="currencyRows.length > 1" class="grid space-x-4 grid-cols-2 mt-8">
          <!-- Currency -->
          <div class="space-y-2">
            <p class="text-base font-bold text-gray-800 ml-1">Failed Payments by Currency</p>
            <div class="text-sm text-brand-brown font-semibold ml-1">
              <p v-if="percentOrRaw === 'percent' && countOrAmount === 'count'">Percentage of failed payments by currency.</p>
              <p v-else-if="percentOrRaw === 'percent' && countOrAmount === 'amount'">Percentage of total failures by invoice amount ($).</p>
              <p v-if="percentOrRaw === 'raw' && countOrAmount === 'count'">Total number of failed payments.</p>
              <p v-else-if="percentOrRaw === 'raw' && countOrAmount === 'amount'">
                Total invoice amount ($) of failed payments converted to USD with current exchange rate.
              </p>
            </div>
            <BasicTable compressed :columns="currencyCols" :data="currencyRows" idProp="value" />
          </div>
          <!-- Recovery Rates -->
          <div class="space-y-2">
            <p class="text-base font-bold text-gray-800 ml-1">{{ percentOrRaw === 'raw' ? 'Recovered Payments' : 'Recovery Rates' }}</p>
            <div class="text-sm text-brand-brown font-semibold ml-1">
              <p v-if="percentOrRaw === 'percent' && countOrAmount === 'count'">
                Percentage of completed campaigns that successfully recovered failed payments.
              </p>
              <p v-else-if="percentOrRaw === 'percent' && countOrAmount === 'amount'">
                Percentage of total invoice failures ($) that that has been recovered.
              </p>
              <p v-if="percentOrRaw === 'raw' && countOrAmount === 'count'">Number of successfully recovered payments.</p>
              <p v-else-if="percentOrRaw === 'raw' && countOrAmount === 'amount'">Total invoice amount recovered after failed payments.</p>
            </div>
            <BasicTable compressed zeroPadding :columns="currencyRecoverCols" :data="currencyRows" idProp="value" />
          </div>
        </div>
        <div class="grid space-x-4 grid-cols-2 mt-8">
          <!-- Card Brand -->
          <div class="space-y-2">
            <p class="text-base font-bold text-gray-800 ml-1">Failed Payments by Card Brand</p>
            <div class="text-sm text-brand-brown font-semibold ml-1">
              <p v-if="percentOrRaw === 'percent' && countOrAmount === 'count'">Percentage of failed payments by card brand.</p>
              <p v-else-if="percentOrRaw === 'percent' && countOrAmount === 'amount'">Percentage of total failures by invoice amount ($).</p>
              <p v-if="percentOrRaw === 'raw' && countOrAmount === 'count'">Total number of failed payments.</p>
              <p v-else-if="percentOrRaw === 'raw' && countOrAmount === 'amount'">Total invoice amount ($) of failed payments.</p>
            </div>
            <BasicTable compressed :columns="cardCols" :data="cardRows" idProp="value" />
          </div>
          <!-- Recovery Rates -->
          <div class="space-y-2">
            <p class="text-base font-bold text-gray-800 ml-1">{{ percentOrRaw === 'raw' ? 'Recovered Payments' : 'Recovery Rates' }}</p>
            <div class="text-sm text-brand-brown font-semibold ml-1">
              <p v-if="percentOrRaw === 'percent' && countOrAmount === 'count'">
                Percentage of completed campaigns that successfully recovered failed payments.
              </p>
              <p v-else-if="percentOrRaw === 'percent' && countOrAmount === 'amount'">
                Percentage of total invoice failures ($) that that has been recovered.
              </p>
              <p v-if="percentOrRaw === 'raw' && countOrAmount === 'count'">Number of successfully recovered payments.</p>
              <p v-else-if="percentOrRaw === 'raw' && countOrAmount === 'amount'">Total invoice amount recovered after failed payments.</p>
            </div>
            <BasicTable compressed zeroPadding :columns="cardRecoverCols" :data="cardRows" idProp="value" />
          </div>
        </div>
      </div>
      <div v-else class="bg-white h-96 relative">
        <div class="text-center absolute h-52 flex items-center justify-center inset-0">
          <el-tooltip effect="dark" placement="bottom-start" popper-class="brand-tooltip">
            <div slot="content">
              <span v-if="filterApplied"> Try changing the filters above. </span>
              <span v-else> We'll have something to show you once more data comes in. </span>
            </div>
            <p class="px-4 py-2 text-brand-blue bg-brand-medium-blue bg-opacity-30 text-sm inline-block rounded-lg font-bold mb-1">
              <span v-if="filterApplied"> No data matches selected filter </span>
              <span v-else> No cancellation insight data yet </span>
            </p>
          </el-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { descending, groups, timeMonth, timeFormat, format, mean, sum, timeDay } from 'd3';
import { OFFER_TYPE, DISCOUNT_TYPE, LOAD_STATE } from '@/store/shared';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import SessionListLoader from '@/components/UI/Loader/SessionListLoader.vue';
import CampaignFilter from '@/components/Dunning/Analytics/DunningCampaignFilter.vue';
import StatsService from '@/api/stats.service';
import BasicTable from '@/components/UI/BasicTable.vue';
import { formatPercent, formatCondensedMoney, formatSmallNumber } from '@/helpers/jsUtils';
import { DUNNING_CAMPAIGN } from '@/helpers/stats-utils';

const breakdowns = ['cardBrand', 'failReason', 'invoiceMonth', 'currency'];
const aggregationFields = [
  'count',
  'invoiceAmountUsd',
  'recoveredAmountUsd',
  'recoveredCount',
  'pendingCount',
  'pendingAmountUsd',
  'lostAmountUsd',
  'lostCount',
];
const monthLabelFormat = timeFormat('%b %y');

const cellFormatter = (d, pct, avg, stdev, cellText, bold) => {
  const step = stdev || avg / 10;
  const diff = (d - avg) / step;
  let cellColor = 'bg-white';
  if (diff < 0) {
    if (diff > -0.5) cellColor = 'bg-red-50';
    else if (diff > -1) cellColor = 'bg-red-100';
    else if (diff > -1.5) cellColor = 'bg-red-200';
    else if (diff > -2) cellColor = 'bg-red-300';
    else if (diff > -3) cellColor = 'bg-red-400';
    else cellColor = 'bg-red-500';
  }
  if (diff > 0) {
    if (diff > 3) cellColor = 'bg-green-500';
    else if (diff > 2) cellColor = 'bg-green-400';
    else if (diff > 1.5) cellColor = 'bg-green-300';
    else if (diff > 1) cellColor = 'bg-green-200';
    else if (diff > 0.5) cellColor = 'bg-green-100';
    else cellColor = 'bg-green-50';
  }
  if (d === 0) {
    cellColor = 'bg-transparent';
  }
  return `<span class="${
    bold ? 'justify-left font-medium' : `${cellColor} justify-center`
  } flex flex-grow px-1 py-1.5 bg-opacity-60 rounded-sm w-full">${cellText}</span>`;
};

export default {
  name: 'DunningBreakdown',
  components: {
    HeaderLoader,
    SessionListLoader,
    CampaignFilter,
    BasicTable,
  },
  data() {
    return {
      OFFER_TYPE,
      DISCOUNT_TYPE,
      counts: [],
      countsLoadState: LOAD_STATE.UNLOADED,
      initialLoading: true,
      countOrAmount: 'count',
      percentOrRaw: 'percent',
      filter: {
        [DUNNING_CAMPAIGN.TRIAL]: ['false'],
      },
      campaignFilters: [DUNNING_CAMPAIGN.TRIAL],
      declineReasonsExpanded: false,
    };
  },
  async created() {
    await this.fetchData();
    this.initialLoading = false;
  },
  computed: {
    sortAggregation() {
      return 'count';
      // return this.countOrAmount === 'count' ? 'count' : 'invoiceAmountUsd';
    },
    aggSuffix() {
      return this.percentOrRaw === 'percent' ? '_pct' : '';
    },
    loading() {
      return this.countsLoadState !== LOAD_STATE.LOADED;
    },
    breakdownCounts() {
      // breakdown by breakdowns, aggregating attributes within each value of each breakdown
      const breakdownCounts = {
        cardBrand: [],
        failReason: [],
        invoiceMonth: [],
        currency: [],
      };
      Object.keys(breakdownCounts).forEach((b) => {
        breakdownCounts[b] = groups(this.counts, (d) => d[b])
          .map((d) => {
            const value = d[0];
            const aggregated = aggregationFields.reduce((acc, f) => {
              acc[f] = d[1].reduce((a, v) => a + v[f], 0);
              return acc;
            }, {});
            return {
              value,
              ...aggregated,
            };
          })
          .sort((first, second) => descending(first[this.sortAggregation], second[this.sortAggregation]));
      });
      return breakdownCounts;
    },
    recentMonths() {
      const months = [...new Set(this.counts.map((d) => d.invoiceMonth))];
      const recentMonths = months
        .sort((a, b) => descending(a, b))
        .slice(0, 4)
        .reverse();
      return recentMonths;
    },
    monthlyBreakdownCounts() {
      const monthlyBreakdownCounts = {
        cardBrand: [],
        failReason: [],
        currency: [],
      };
      Object.keys(monthlyBreakdownCounts).forEach((b) => {
        monthlyBreakdownCounts[b] = this.recentMonths.map((m) => {
          const monthlyCounts = this.counts.filter((d) => d.invoiceMonth === m);
          // calculate totals for this month to use for calculating percentages
          const monthlyTotals = aggregationFields.reduce((acc, f) => {
            acc[f] = monthlyCounts.reduce((a, v) => a + v[f], 0);
            return acc;
          }, {});
          const breakdown = groups(
            this.counts.filter((d) => d.invoiceMonth === m),
            (d) => d[b]
          ).map((d) => {
            // for each value of each breakdown (e.g. d: visa, b: cardBrand, m: 2023-03)
            const value = d[0];
            const aggregated = aggregationFields.reduce((acc, f) => {
              acc[f] = d[1].reduce((a, v) => a + v[f], 0);
              acc[`${f}_pct`] = acc[f] / monthlyTotals[f]; // [f] is the aggregation field (e.g. count, recoveredAmountUsd, etc.
              return acc;
            }, {});
            return {
              value,
              ...aggregated,
            };
          });
          return {
            month: m,
            breakdown,
          };
        });
      });
      return monthlyBreakdownCounts;
    },
    // flatten out all data for the table
    flattenedData() {
      // flattenedData[invoice-month_card-brand] = { invoiceMonth, cardBrand, ...aggregations }
      const flattenedData = {};
      // build from monthlyBreakdownCounts
      ['cardBrand', 'failReason', 'currency'].forEach((breakdown) => {
        this.monthlyBreakdownCounts[breakdown].forEach((m) => {
          m.breakdown.forEach((breakdownValue) => {
            flattenedData[`${m.month}_${breakdownValue.value}`] = {
              invoiceMonth: m.month,
              [breakdown]: breakdownValue.value,
              ...breakdownValue,
            };
          });
        });
      });
      return flattenedData;
    },
    failureDataColumns() {
      const rawField = this.countOrAmount === 'count' ? 'count' : 'invoiceAmountUsd';
      const field = this.percentOrRaw === 'percent' ? `${rawField}_pct` : rawField;
      const totalPercentColumn = {
        label: 'Total',
        prop: 'totalPercent',
        align: 'left',
        valueAccessor: (row) => {
          // average across all months (not weighted average)
          const values = this.recentMonths.map((m) => {
            return this.flattenedData[`${m}_${row.value}`] ? this.flattenedData[`${m}_${row.value}`][field] || 0 : 0;
          });
          if (this.percentOrRaw === 'percent') {
            return mean(values);
          }
          return sum(values);
        },
        // eslint-disable-next-line no-nested-ternary
        formatter: this.percentOrRaw === 'percent' ? formatPercent : this.countOrAmount === 'count' ? formatSmallNumber : formatCondensedMoney,
      };
      const monthByMonth = this.recentMonths.map((m) => ({
        // add a day to the month to account for timezones
        label: monthLabelFormat(new Date(timeDay.offset(new Date(m), 1))),
        prop: m,
        align: 'left',
        valueAccessor: (row) => {
          return this.flattenedData[`${m}_${row.value}`] ? this.flattenedData[`${m}_${row.value}`][field] || 0 : 0;
        },
        // eslint-disable-next-line no-nested-ternary
        formatter: this.percentOrRaw === 'percent' ? formatPercent : this.countOrAmount === 'count' ? formatSmallNumber : formatCondensedMoney,
      }));
      return [totalPercentColumn, ...monthByMonth];
    },
    recoveryDataColumns() {
      const rawField = this.countOrAmount === 'count' ? 'recoveredCount' : 'recoveredAmountUsd';
      const field = this.percentOrRaw === 'percent' ? `${rawField}_pct` : rawField;
      const totalPercentColumn = {
        label: 'Total',
        prop: 'totalPercent',
        align: 'left',
        valueAccessor: (row) => {
          if (this.percentOrRaw === 'percent') {
            const denominatorValues = this.recentMonths.map((m) => {
              return this.flattenedData[`${m}_${row.value}`]
                ? this.flattenedData[`${m}_${row.value}`][this.countOrAmount === 'count' ? 'count' : 'invoiceAmountUsd'] || 0
                : 0;
            });
            const numeratorValues = this.recentMonths.map((m) => {
              return this.flattenedData[`${m}_${row.value}`] ? this.flattenedData[`${m}_${row.value}`][rawField] || 0 : 0;
            });
            return sum(numeratorValues) / sum(denominatorValues);
          }
          // average across all months (not weighted average)
          const values = this.recentMonths.map((m) => {
            return this.flattenedData[`${m}_${row.value}`] ? this.flattenedData[`${m}_${row.value}`][field] || 0 : 0;
          });
          return sum(values);
        },
        formatter: (value, row) => {
          if (this.percentOrRaw === 'percent' || value === 0) {
            const cellText = formatPercent(value);
            return cellFormatter(value, value, 0.4, 0.4, cellText, true);
          }
          // calculate percentage recovered
          const denominatorValues = this.recentMonths.map((m) => {
            return this.flattenedData[`${m}_${row.value}`]
              ? this.flattenedData[`${m}_${row.value}`][this.countOrAmount === 'count' ? 'count' : 'invoiceAmountUsd'] || 0
              : 0;
          });
          const numeratorValues = this.recentMonths.map((m) => {
            return this.flattenedData[`${m}_${row.value}`] ? this.flattenedData[`${m}_${row.value}`][rawField] || 0 : 0;
          });
          const pct = sum(numeratorValues) / sum(denominatorValues);
          const cellText = this.countOrAmount === 'count' ? formatSmallNumber(value) : formatCondensedMoney(value);
          return cellFormatter(pct, pct, 0.4, 0.4, cellText, true);
        },
        // formatter: this.percentOrRaw === 'percent' ? formatPercent : this.countOrAmount === 'count' ? formatSmallNumber : formatCondensedMoney,
      };
      const monthByMonth = this.recentMonths.map((m) => ({
        label: monthLabelFormat(new Date(timeDay.offset(new Date(m), 1))),
        prop: m,
        align: 'center',
        valueAccessor: (row) => {
          if (this.percentOrRaw === 'percent') {
            const numerator = this.flattenedData[`${m}_${row.value}`] ? this.flattenedData[`${m}_${row.value}`][rawField] || 0 : 0;
            // look at total count or total invoice amount for that month and reason for decline
            const denominator = this.flattenedData[`${m}_${row.value}`]
              ? this.flattenedData[`${m}_${row.value}`][this.countOrAmount === 'count' ? 'count' : 'invoiceAmountUsd'] || 0
              : 0;
            return numerator / denominator;
          }
          return this.flattenedData[`${m}_${row.value}`] ? this.flattenedData[`${m}_${row.value}`][field] || 0 : 0;
        },
        formatter: (value, row) => {
          if (this.percentOrRaw === 'percent' || value === 0) {
            const cellText = formatPercent(value);
            return cellFormatter(value, value, 0.4, 0.4, cellText);
          }
          // calculate percentage recovered
          const pct = this.flattenedData[`${m}_${row.value}`]
            ? this.flattenedData[`${m}_${row.value}`][rawField] /
              this.flattenedData[`${m}_${row.value}`][this.countOrAmount === 'count' ? 'count' : 'invoiceAmountUsd']
            : 0;
          const cellText = this.countOrAmount === 'count' ? formatSmallNumber(value) : formatCondensedMoney(value);
          return cellFormatter(pct, pct, 0.4, 0.4, cellText);
        },
      }));
      return [totalPercentColumn, ...monthByMonth];
    },
    failReasonCols() {
      const breakdownColumn = {
        label: 'Decline Reason',
        prop: 'Decline Reason',
        align: 'left',
        valueAccessor: (row) => row.value,
        // eslint-disable-next-line no-confusing-arrow
        formatter: (value) =>
          // eslint-disable-next-line implicit-arrow-linebreak
          value
            ? `<span class="font-medium">
            ${value.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}
           </span>`
            : 'No Decline Reason',
      };
      return [breakdownColumn, ...this.failureDataColumns];
    },
    failReasonRecoverCols() {
      const breakdownColumn = {
        label: 'Decline Reason',
        prop: 'Decline Reason',
        align: 'left',
        valueAccessor: (row) => row.value,
        // eslint-disable-next-line no-confusing-arrow
        formatter: (value) =>
          // eslint-disable-next-line implicit-arrow-linebreak
          value
            ? `<span class="font-medium">
            ${value.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}
           </span>`
            : 'No Decline Reason',
      };
      return [breakdownColumn, ...this.recoveryDataColumns];
    },
    declineReasonRows() {
      const totalSum = this.counts.map((d) => d[this.sortAggregation]).reduce((a, b) => a + b, 0);
      return this.breakdownCounts.failReason
        .filter((d) => d[this.sortAggregation] > totalSum / this.recentMonths.length / 30)
        .sort((a, b) => descending(a[this.sortAggregation], b[this.sortAggregation]))
        .map((d) => {
          return {
            value: d.value,
            breakdown: 'failReason',
          };
        });
    },
    declineReasonDisplayedRows() {
      return this.declineReasonRows.filter((d, i) => i <= 5 || this.declineReasonsExpanded);
    },
    declineReasonsHidden() {
      return this.declineReasonRows.length > 5 && !this.declineReasonsExpanded;
    },
    ///////////////
    // CARD BRAND
    ///////////////
    cardCols() {
      const breakdownColumn = {
        label: 'Card Brand',
        prop: 'Card Brand',
        align: 'left',
        valueAccessor: (row) => row.value,
        formatter: (value) => (value ? `<span class="font-medium">${value.charAt(0).toUpperCase() + value.slice(1)}</span>` : 'No Card Brand'),
      };
      return [breakdownColumn, ...this.failureDataColumns];
    },
    cardRecoverCols() {
      const breakdownColumn = {
        label: 'Card Brand',
        prop: 'Card Brand',
        align: 'left',
        valueAccessor: (row) => row.value,
        formatter: (value) => (value ? `<span class="font-medium">${value.charAt(0).toUpperCase() + value.slice(1)}</span>` : 'No Card Brand'),
      };
      return [breakdownColumn, ...this.recoveryDataColumns];
    },
    cardRows() {
      const totalSum = this.counts.map((d) => d[this.sortAggregation]).reduce((a, b) => a + b, 0);
      return this.breakdownCounts.cardBrand
        .filter((d) => d[this.sortAggregation] > totalSum / this.recentMonths.length / 50)
        .sort((a, b) => descending(a[this.sortAggregation], b[this.sortAggregation]))
        .map((d) => {
          return {
            value: d.value,
            breakdown: 'cardBrand',
          };
        });
    },
    ///////////////
    // CARD BRAND
    ///////////////
    currencyCols() {
      const breakdownColumn = {
        label: 'Currency',
        prop: 'Currency',
        align: 'left',
        valueAccessor: (row) => row.value,
        formatter: (value) => (value ? `<span class="font-medium">${value.charAt(0).toUpperCase() + value.slice(1)}</span>` : 'No Currency'),
      };
      return [breakdownColumn, ...this.failureDataColumns];
    },
    currencyRecoverCols() {
      const breakdownColumn = {
        label: 'Currency',
        prop: 'Currency',
        align: 'left',
        valueAccessor: (row) => row.value,
        formatter: (value) => (value ? `<span class="font-medium">${value.charAt(0).toUpperCase() + value.slice(1)}</span>` : 'No Currency'),
      };
      return [breakdownColumn, ...this.recoveryDataColumns];
    },
    currencyRows() {
      const totalSum = this.counts.map((d) => d[this.sortAggregation]).reduce((a, b) => a + b, 0);
      return this.breakdownCounts.currency
        .filter((d) => d[this.sortAggregation] > totalSum / this.recentMonths.length / 50)
        .sort((a, b) => descending(a[this.sortAggregation], b[this.sortAggregation]))
        .map((d) => {
          return {
            value: d.value,
            breakdown: 'currency',
          };
        });
    },
  },
  methods: {
    async fetchData() {
      const startDate = timeMonth.floor(timeMonth.offset(new Date(), -4));
      const endDate = timeMonth.ceil(timeMonth.offset(new Date(), -1));
      // set startDate and endDate to UTC midnight
      startDate.setUTCHours(0, 0, 0, 0);
      endDate.setUTCHours(0, 0, 0, 0);
      const mappedFilter = Object.keys(this.filter).reduce((acc, k) => {
        if (this.filter[k].length === 1) {
          acc[k] = this.filter[k][0];
        }
        return acc;
      }, {});
      mappedFilter.startDate = startDate;
      mappedFilter.endDate = endDate;
      try {
        this.countsLoadState = LOAD_STATE.LOADING;
        const counts = await StatsService.dunningCampaignCountUsd({
          filter: mappedFilter,
          breakdown: breakdowns,
        });
        this.counts = counts;
        this.countsLoadState = LOAD_STATE.LOADED;
      } catch (e) {
        console.log(e);
        this.countsLoadState = LOAD_STATE.ERROR_LOADING;
      }
    },
    onFilterUpdate(newFilter) {
      this.filter = newFilter;
      this.fetchData();
    },
    filterApplied() {
      return Object.keys(this.filter).length > 0 || this.searchInput.length > 0;
    },
  },
};
</script>
